/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { View, StyleSheet } from 'react-native';
import { CellMeasurerCache, List } from 'react-virtualized';

import BuilderMenu from './BuilderMenu';
import { FormElementList } from './FormElementList';
import { BuilderStateProvider } from './BuilderContext';
import { ClickAway } from '../uiold';

import { Produce } from './useImmer';
import {
  EvaluationFormDefinition,
  FormElementDefinition,
} from '../../types/shared/EvaluationForm';

type FieldState = {
  activeFieldId: string | null | undefined;
  activeFieldIndex: number | null | undefined;
};

type Props = {
  formDef: EvaluationFormDefinition;
  update: Produce<EvaluationFormDefinition>;
};

let arrayMoveMutate = (
  array: Array<FormElementDefinition>,
  from: number,
  to: number,
) => {
  let startIndex = to < 0 ? array.length + to : to;
  let item = array.splice(from, 1)[0];
  array.splice(startIndex, 0, item as any);
};

function FormBuilder(props: Props) {
  let { formDef, update } = props;
  let [activeFieldState, setActiveField] = useState<FieldState>({
    activeFieldId: null,
    activeFieldIndex: null,
  });
  let listRef = useRef<List>(null);
  let cacheRef = useRef<CellMeasurerCache>(null);
  let preActiveFieldStateRef = useRef<FieldState>({
    activeFieldId: null,
    activeFieldIndex: null,
  });

  let builderState = useMemo(
    () => ({
      ...activeFieldState,
      setActiveField,
      update,
    }),
    [activeFieldState, update],
  );

  // Active field have different height compared to inactive field.
  // Only recalculate the row height when the active field is different.
  useEffect(() => {
    // When active field changed, the row index might stay the same
    // but the id changed because the previous field has been deleted
    // and replaced by the next field.
    if (cacheRef.current) {
      // We need to clear all saved height since all row after the deleted row
      // will be shifted by one.
      // Could be optimized by shifting the cache instead of clearing them.
      cacheRef.current?.clearAll();
    }
    if (listRef.current) {
      listRef.current.recomputeRowHeights(
        // Only recompute affected row.
        Math.min(
          (preActiveFieldStateRef.current &&
            preActiveFieldStateRef.current.activeFieldIndex) ||
            0,
          activeFieldState.activeFieldIndex || 0,
        ),
      );
    }
    preActiveFieldStateRef.current = activeFieldState;
  }, [activeFieldState]);

  let onSortEnd = ({ oldIndex, newIndex }: any) => {
    if (oldIndex === newIndex) {
      return;
    }
    let field = formDef.fields[oldIndex];
    update((draft) => arrayMoveMutate(draft.fields, oldIndex, newIndex));
    // Set the moved field as active after reordering.
    setActiveField({
      activeFieldId: field?.id as any,
      activeFieldIndex: newIndex,
    });
  };

  let onClickAway = useCallback(() => {
    setActiveField({
      activeFieldId: null,
      activeFieldIndex: null,
    });
  }, []);

  return (
    <BuilderStateProvider value={builderState}>
      <ClickAway onClickAway={onClickAway}>
        <View style={styles.root}>
          <FormElementList
            {...props}
            mode="builder"
            innerRef={listRef}
            cacheRef={cacheRef}
            {...{
              onSortEnd: onSortEnd,
              lockAxis: 'y',
              lockToContainerEdges: true,
              useDragHandle: true,
            }}
          />
          {/* BuilderMenu should be inside the ClickAway so
          clicking the menu doesn't clear the focus from the form element list */}
          <BuilderMenu />
        </View>
      </ClickAway>
    </BuilderStateProvider>
  );
}

let styles = StyleSheet.create({
  root: {
    flex: 1,
    maxWidth: 768,
    flexDirection: 'row',
  },
});

export default FormBuilder;
